import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Printer, Stethoscope } from "lucide-react";
import { fmtDate, ageDisplay } from "@/lib/format";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PublicPrescription() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");

  useEffect(() => {
    axios.get(`${API}/share/prescription/${token}`).then((r) => setData(r.data)).catch(() => setErr("Not found"));
  }, [token]);

  if (err) return <div className="min-h-screen flex items-center justify-center text-[#8A9992]">Prescription not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-[#8A9992]">Loading…</div>;

  const { consultation: c, doctor: d, patient: p } = data;

  return (
    <div className="min-h-screen bg-[#FDFBF7] py-10 px-4">
      <div className="max-w-3xl mx-auto">
        <div className="no-print flex justify-end mb-4">
          <button onClick={() => window.print()} className="btn-emerald"><Printer size={16} /> Print / Save PDF</button>
        </div>
        <div className="card-soft p-10">
          <header className="flex items-start justify-between pb-6 border-b-2 border-[#0A4C36]">
            <div>
              <div className="flex items-center gap-3 mb-2">
                {d?.logo_path ? (
                  <img
                    src={`${API}/public/logo/${d.id}`}
                    alt="Clinic logo"
                    className="w-12 h-12 rounded-2xl object-cover bg-white"
                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                    data-testid="rx-logo"
                  />
                ) : (
                  <div className="w-12 h-12 rounded-2xl bg-[#0A4C36] text-white flex items-center justify-center"><Stethoscope size={20} /></div>
                )}
                <div>
                  <div className="text-2xl font-semibold text-[#0A4C36]" style={{ fontFamily: "Instrument Sans" }}>Dr. {d?.name?.replace(/^Dr\.?\s*/i, "")}</div>
                  <div className="text-xs text-[#6C7B72]">{d?.qualification} {d?.specialties?.length ? `· ${d.specialties.join(", ")}` : ""}</div>
                </div>
              </div>
              {d?.clinic_name && <div className="text-sm font-semibold text-[#1F2A24]">{d.clinic_name}</div>}
              <div className="text-xs text-[#6C7B72] whitespace-pre-line">{d?.address}</div>
              <div className="text-xs text-[#6C7B72] mt-1">
                {d?.mobile && <>Mobile: {d.mobile} · </>}
                {d?.register_no && <>Reg No: {d.register_no}</>}
                {d?.state_medical_council && <> · {d.state_medical_council}</>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-widest uppercase text-[#8A9992]">Prescription</div>
              <div className="text-xs text-[#1F2A24] mt-1">{fmtDate(c?.created_at)}</div>
            </div>
          </header>

          <section className="grid grid-cols-2 gap-6 py-6 border-b border-[#EFECE5]">
            <Info label="Patient" value={p?.name} />
            <Info label="Age / Gender" value={`${ageDisplay(p)} · ${p?.gender}`} />
            <Info label="Chief complaint" value={c?.chief_complaint} full />
            {c?.diagnosis && <Info label="Diagnosis" value={c.diagnosis} full />}
          </section>

          {c?.vitals && Object.values(c.vitals).some(Boolean) && (
            <section className="py-4 border-b border-[#EFECE5] text-xs text-[#1F2A24]">
              <span className="font-semibold mr-2">Vitals:</span>
              {["bp", "pulse", "temp", "spo2", "weight", "height"].map((k) => c.vitals[k] ? <span key={k} className="chip mr-2">{k.toUpperCase()}: {c.vitals[k]}</span> : null)}
            </section>
          )}

          {c?.medications?.length > 0 && (
            <section className="py-6 border-b border-[#EFECE5]">
              <div className="font-semibold text-[#0A4C36] mb-3" style={{ fontFamily: "Instrument Sans" }}>℞ Medications</div>
              <ol className="space-y-3">
                {c.medications.map((m, i) => {
                  const dose = m.dose ? `${m.dose.morning || 0}-${m.dose.afternoon || 0}-${m.dose.night || 0}` : (m.frequency || "");
                  const when = m.when_food === "AF" ? `${m.mins_food ? m.mins_food + " min " : ""}after food` : m.when_food === "BF" ? `${m.mins_food ? m.mins_food + " min " : ""}before food` : (m.when_food === "anytime" ? "anytime" : "");
                  const dur = m.duration ? `${m.duration} ${m.duration_unit || "day"}${m.duration > 1 ? "s" : ""}` : (m.duration || "");
                  const strength = m.strength || m.dosage || "";
                  return (
                    <li key={i} className="flex items-start gap-3">
                      <div className="w-6 h-6 rounded-full bg-[#E6F0EC] text-[#0A4C36] text-xs font-semibold flex items-center justify-center mt-0.5">{i + 1}</div>
                      <div>
                        <div className="font-semibold text-[#1F2A24]">
                          {m.name} {strength && <span className="text-[#6C7B72] font-normal">— {strength}</span>} {m.form && <span className="text-[10px] uppercase tracking-widest text-[#8A9992] ml-1">{m.form}</span>}
                        </div>
                        <div className="text-xs text-[#6C7B72]">{[dose, when, dur, m.instructions].filter(Boolean).join(" · ")}</div>
                      </div>
                    </li>
                  );
                })}
              </ol>
            </section>
          )}

          {c?.lab_orders?.length > 0 && (
            <section className="py-6 border-b border-[#EFECE5]">
              <div className="font-semibold text-[#0A4C36] mb-3" style={{ fontFamily: "Instrument Sans" }}>Investigations</div>
              <ul className="space-y-1">
                {c.lab_orders.map((l, i) => (
                  <li key={i} className="text-sm text-[#1F2A24]">• {l.name} {l.urgency && l.urgency !== "routine" && <span className="text-[#8A5A34] uppercase text-xs">({l.urgency})</span>} {l.notes && <span className="text-[#6C7B72]">— {l.notes}</span>}</li>
                ))}
              </ul>
            </section>
          )}

          {(c?.advice_list?.length > 0 || c?.advice || c?.follow_up_date || c?.follow_up) && (
            <section className="py-6">
              {(c.advice_list?.length > 0 || c.advice) && <BulletInfo label="Advice" items={c.advice_list} fallback={c.advice} full />}
              {(c.follow_up_date || c.follow_up) && (
                <div className="mt-3">
                  <Info label="Follow-up" value={c.follow_up_date ? `${fmtDate(c.follow_up_date)}${c.follow_up_time ? " · " + c.follow_up_time : ""}${c.follow_up_note ? " — " + c.follow_up_note : ""}` : c.follow_up} full />
                </div>
              )}
            </section>
          )}

          <footer className="pt-8 mt-6 border-t border-[#EFECE5] flex justify-end">
            <div className="text-right">
              <div className="text-xs text-[#6C7B72]">Signature</div>
              <div className="text-lg font-semibold text-[#0A4C36] mt-2" style={{ fontFamily: "Instrument Sans" }}>Dr. {d?.name?.replace(/^Dr\.?\s*/i, "")}</div>
              <div className="text-xs text-[#6C7B72]">{d?.qualification}</div>
            </div>
          </footer>
        </div>
      </div>
    </div>
  );
}

function Info({ label, value, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] tracking-widest uppercase text-[#8A9992]">{label}</div>
      <div className="text-sm text-[#1F2A24] mt-1 whitespace-pre-line">{value || "—"}</div>
    </div>
  );
}

function BulletInfo({ label, items, fallback, full }) {
  const list = Array.isArray(items) && items.length ? items : (fallback ? [fallback] : []);
  if (!list.length) return null;
  return (
    <div className={full ? "col-span-2" : ""}>
      <div className="text-[10px] tracking-widest uppercase text-[#8A9992]">{label}</div>
      <ul className="mt-1 space-y-1">
        {list.map((v, i) => (
          <li key={i} className="text-sm text-[#1F2A24] flex items-start gap-2">
            <span className="mt-2 w-1.5 h-1.5 rounded-full bg-[#0A4C36] shrink-0" />
            <span className="leading-relaxed">{v}</span>
          </li>
        ))}
      </ul>
    </div>
  );
}
